import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { MapPin, Phone, Download, Info, Mail, Megaphone } from 'lucide-react';
import { useShul, isShulPubliclyVisible } from '../hooks/useShuls';
import { useZmanim, useZmanimForDates } from '../hooks/useZmanim';
import { useShulImages } from '../hooks/useShulImages';
import { detectDayType } from '../utils/dayTypeDetector';
import DayLabels from '../components/common/DayLabels';
import { resolveShulSchedule } from '../utils/scheduleResolver';
import { getTodayString } from '../utils/dateShortcuts';
import DaySelector from '../components/public/DaySelector';
import NavigateButton from '../components/directory/NavigateButton';
import ShulShiurim from '../components/directory/ShulShiurim';
import { DEFAULT_AREA_ID, getArea } from '../config/areas';
import { navTargetOf } from '../utils/geo';
import { activeMessages, pickLang } from '../utils/listingDisplay';
import { PosterFile, ResolvedScheduleTime } from '../types';
import { EVE_PRAYERS, getGoldSpan } from '../utils/minyanDay';
import { breadcrumbJsonLd, pageUrl, placeJsonLd } from '../utils/seo';


export default function ShulPage() {
  const { shulId = '' } = useParams<{ shulId: string }>();
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';

  const [selectedDate, setSelectedDate] = useState(getTodayString());
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { shul, loading: shulLoading, error: shulError } = useShul(shulId);
  const { zmanim, loading: zmanimLoading, error: zmanimError } =
    useZmanim(selectedDate);
  const { downloadPoster } = useShulImages(shulId);
  // Head tags + structured data (docs/seo-plan.md); hidden / missing shuls stay out of Google.
  const seoLang = isHebrew ? 'he' : 'en';
  const seoShul = shul && isShulPubliclyVisible(shul) ? shul : null;
  const seoName = seoShul ? (isHebrew ? seoShul.nameHe : seoShul.nameEn) || seoShul.nameHe || seoShul.nameEn : '';
  const seoNotFound = !shulLoading && !shulError && !seoShul;
  usePageTitle(
    seoShul ? t('seo.shul', { name: seoName }) : seoNotFound ? t('shul.notFound') : undefined,
    seoShul
      ? {
          description: t('seo.desc.shul', { name: seoName }),
          image: seoShul.photoUrl,
          jsonLd: [
            placeJsonLd({
              type: 'Synagogue',
              name: seoName,
              otherName: isHebrew ? seoShul.nameEn : seoShul.nameHe,
              description: isHebrew ? seoShul.descriptionHe || seoShul.description : seoShul.description || seoShul.descriptionHe,
              url: pageUrl(`/shul/${seoShul.id}`, seoLang),
              lang: seoLang,
              address: seoShul.address,
              lat: seoShul.lat,
              lng: seoShul.lng,
              phone: seoShul.phone,
              image: seoShul.photoUrl,
            }),
            breadcrumbJsonLd([
              { name: t('seo.breadcrumbHome'), url: pageUrl('/', seoLang) },
              { name: t('directory.categoryPlural.shul'), url: pageUrl('/shuls', seoLang) },
              { name: seoName, url: pageUrl(`/shul/${seoShul.id}`, seoLang) },
            ]),
          ],
        }
      : { noindex: seoNotFound }
  );

  const loading = shulLoading || zmanimLoading;
  const error = shulError || zmanimError;

  const dayType = zmanim ? detectDayType(zmanim) : null;


  const schedule = useMemo(() => {
    if (!shul || !zmanim || !dayType) return [];
    return resolveShulSchedule(shul, selectedDate, dayType, zmanim);
  }, [shul, zmanim, dayType, selectedDate]);

  const goldSpan = getGoldSpan(selectedDate);
  const spanDates = goldSpan ? [goldSpan.eve, ...goldSpan.days] : [];
  const spanZmanim = useZmanimForDates(spanDates);
  const span = useMemo(() => {
    if (!goldSpan || !shul) return null;
    return spanDates.map((date) => {
      const z = spanZmanim[date];
      const isEve = date === goldSpan.eve;
      const all = z ? resolveShulSchedule(shul, date, detectDayType(z), z) : [];
      return { date, z, isEve, rows: isEve ? all.filter((r) => EVE_PRAYERS.includes(r.prayerType)) : all };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shul, spanZmanim, selectedDate]);

  const longDate = (date: string) =>
    new Intl.DateTimeFormat(isHebrew ? 'he' : 'en', { weekday: 'long', day: 'numeric', month: 'long' }).format(
      new Date(`${date}T12:00:00`)
    );

  const candleRow = (time: string) => (
    <div className="flex items-center justify-between gap-3 rounded-[16px] border border-ink/10 bg-fog px-4 py-3">
      <span className="text-sm font-medium text-ink">{t('zmanim.candleLighting')}</span>
      <span className="text-base font-bold tabular-nums text-ink">{time}</span>
    </div>
  );

  const renderRows = (rows: ResolvedScheduleTime[]) => (
          <div className="bg-paper rounded-[16px] border border-ink/10 p-4">
            <ul className="divide-y divide-ink/10">
              {rows.map((row) => {
                const note = isHebrew
                  ? row.noteHe || row.note
                  : row.note || row.noteHe;
                const changeNote = isHebrew
                  ? row.overrideNoteHe || row.overrideNote
                  : row.overrideNote || row.overrideNoteHe;
                return (
                  <li
                    key={`${row.scheduleId}-${row.prayerType}-${row.displayTime}`}
                    className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                  >
                    <span className="text-sm text-ink min-w-0">
                      {t(`prayers.${row.prayerType}`)}
                      {note ? (
                        <span className="text-ash">
                          {' — '}
                          {note}
                        </span>
                      ) : null}
                      {row.isOverride && (
                        <span className="block text-xs font-medium text-gold-700">
                          {t('shul.changedForDate')}
                          {changeNote ? ` — ${changeNote}` : ''}
                        </span>
                      )}
                    </span>
                    <span
                      className={`text-base font-bold tabular-nums shrink-0 ${
                        row.isOverride ? 'text-gold-700' : 'text-ink'
                      }`}
                    >
                      {row.displayTime}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
  );

  const handleDownload = async (poster: PosterFile) => {
    setDownloadingId(poster.id);
    try {
      await downloadPoster(poster);
    } catch {
      // Surface via alert so public users get feedback without admin error UI.
      window.alert(t('common.error'));
    } finally {
      setDownloadingId(null);
    }
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        <div className="space-y-3 animate-pulse">
          <div className="h-40 w-full rounded-[16px] bg-fog" />
          <div className="h-8 w-2/3 rounded-[10px] bg-fog" />
          <div className="h-4 w-1/2 rounded bg-fog" />
          <div className="h-4 w-1/3 rounded bg-fog" />
          <div className="h-6 w-24 rounded-[10px] bg-fog mt-2" />
        </div>
        <div className="space-y-3">
          <div className="h-5 w-40 rounded bg-fog animate-pulse" />
          {[0, 1].map((i) => (
            <div
              key={i}
              className="h-36 rounded-[16px] bg-fog animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <p className="text-center text-sm text-danger-500 py-8">{t('common.error')}</p>
      </div>
    );
  }

  if (!shul) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <p className="text-center text-sm text-ash py-8">{t('shul.notFound')}</p>
      </div>
    );
  }

  // Hide pending / inactive shuls from the public detail page.
  if (!isShulPubliclyVisible(shul)) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-6">
        <p className="text-center text-sm text-ash py-8">{t('shul.notFound')}</p>
      </div>
    );
  }

  const description = isHebrew
    ? shul.descriptionHe || shul.description
    : shul.description || shul.descriptionHe;

  const messages = activeMessages(shul.messages, new Date().toISOString());
  const note = pickLang(shul.noteEn, shul.noteHe, isHebrew);

  const todayStr = getTodayString();
  const formattedDate = new Intl.DateTimeFormat(isHebrew ? 'he' : 'en', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(selectedDate + 'T12:00:00'));

  const posters = shul.posters || [];
  const shulName = isHebrew ? shul.nameHe : shul.nameEn;
  // Shuls always show their exact location.
  const navTarget = navTargetOf(
    {
      addressEn: shul.address,
      addressHe: '',
      lat: shul.lat,
      lng: shul.lng,
      areaId: DEFAULT_AREA_ID,
      showExactLocation: true,
    },
    getArea(DEFAULT_AREA_ID),
    isHebrew
  );

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {shul.photoUrl && (
        <div className="w-full overflow-hidden rounded-[16px] border border-ink/10 bg-fog">
          <img
            src={shul.photoUrl}
            alt={shulName}
            className="w-full max-h-56 sm:max-h-72 object-cover"
          />
        </div>
      )}

      <header className="space-y-3">
        <h1 className="text-2xl font-bold text-ink">{shulName}</h1>

        {/* Messages and the note first, right under the name (Yoel, 2026-10-02). */}
        {messages.length > 0 && (
          <section aria-label={t('directory.detail.messages')} className="space-y-2">
            {messages.map((m) => (
              <p key={m.id} className="flex gap-2 rounded-[16px] bg-weekday-50 text-weekday-700 p-3 text-sm whitespace-pre-line">
                <Megaphone className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
                <span>{pickLang(m.textEn, m.textHe, isHebrew)}</span>
              </p>
            ))}
          </section>
        )}

        {note && (
          <p className="flex gap-2 rounded-[16px] bg-gold-50 text-gold-700 p-3 text-sm font-medium whitespace-pre-line">
            <Info className="w-4 h-4 mt-0.5 shrink-0" aria-hidden />
            <span>{note}</span>
          </p>
        )}

        {shul.nusach && (
          <span className="inline-block text-xs px-2.5 py-1 rounded-[10px] font-medium bg-fog text-ink">
            {t(`nusach.${shul.nusach}`)}
          </span>
        )}

        {shul.address && (
          shul.lat && shul.lng ? (
            <Link
              to={`/shuls?view=map&shul=${shul.id}`}
              className="group relative inline-flex items-center gap-2 text-sm text-ink hover:opacity-70 transition-opacity"
              title={t('shul.showOnMap')}
            >
              <MapPin className="w-4 h-4 shrink-0 text-ash" />
              <span>{shul.address}</span>
              <span className="pointer-events-none absolute start-6 top-full mt-0.5 text-xs text-ash opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                {t('shul.showOnMap')}
              </span>
            </Link>
          ) : (
            <p className="inline-flex items-center gap-2 text-sm text-ink">
              <MapPin className="w-4 h-4 shrink-0 text-ash" />
              <span>{shul.address}</span>
            </p>
          )
        )}

        {shul.phone && (
          <a
            href={`tel:${shul.phone}`}
            className="inline-flex items-center gap-2 text-sm font-medium text-ink hover:opacity-70 transition-opacity"
            aria-label={t('shul.callShul')}
          >
            <Phone className="w-4 h-4 shrink-0" />
            <span>{shul.phone}</span>
          </a>
        )}

        {navTarget && (
          <div>
            <NavigateButton target={navTarget} />
          </div>
        )}

        {description && (
          <p className="text-sm text-ink leading-relaxed">{description}</p>
        )}

        <DayLabels date={selectedDate} />

        <DaySelector selectedDate={selectedDate} onChange={setSelectedDate} />
      </header>

      <section className="space-y-3">
        <h2 className="text-lg font-semibold text-ink">
          {selectedDate === todayStr
            ? t('shul.todaysSchedule')
            : t('shul.scheduleFor', { date: formattedDate })}
        </h2>

        {span ? (
          span.map(({ date, rows, z }) => (
            <div key={date} className="space-y-2">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-ink">{longDate(date)}</h3>
                <DayLabels date={date} />
              </div>
              {z?.candleLighting && candleRow(z.candleLighting)}
              {rows.length === 0 ? (
                <p className="text-sm text-ash">{t('shul.noTimesForSelectedDay')}</p>
              ) : (
                renderRows(rows)
              )}
            </div>
          ))
        ) : (
          <>
            {zmanim?.candleLighting && candleRow(zmanim.candleLighting)}
            {schedule.length === 0 ? (
              <p className="text-center text-sm text-ash py-8">
                {t('shul.noTimesForSelectedDay')}
              </p>
            ) : (
              renderRows(schedule)
            )}
          </>
        )}
      </section>

      <ShulShiurim shulId={shul.id} />

      {posters.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-lg font-semibold text-ink">{t('shul.posters')}</h2>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {posters.map((poster) => (
              <li
                key={poster.id}
                className="flex flex-col gap-2 rounded-[16px] border border-ink/10 bg-paper p-3"
              >
                <img
                  src={poster.thumbnailUrl}
                  alt={poster.fileName}
                  className="w-full aspect-[3/4] max-h-64 object-contain rounded-[10px] bg-fog"
                />
                <p className="text-sm text-ink truncate" title={poster.fileName}>
                  {poster.fileName}
                </p>
                <button
                  type="button"
                  disabled={downloadingId === poster.id}
                  onClick={() => handleDownload(poster)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-[10px] bg-ink text-paper text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <Download className="w-4 h-4 shrink-0" />
                  {poster.contentType === 'pdf'
                    ? t('shul.downloadPdf')
                    : t('shul.downloadImage')}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <Link
        to={`/contact?topic=listingQuestion&listing=${encodeURIComponent(shul.id)}`}
        className="inline-flex items-center gap-1.5 text-sm text-ink underline min-h-[44px]"
      >
        <Mail className="w-4 h-4" aria-hidden />
        {t('contact.aboutThisListing')}
      </Link>
    </div>
  );
}
